import { score2CalculatorKeys as k } from "@/i18n/score2-calculator-keys"
import type { Country } from "@/i18n/config"

/**
 * SCORE2: the European Society of Cardiology's 10-year risk of fatal AND
 * non-fatal cardiovascular disease in apparently healthy adults.
 *
 * Source: SCORE2 working group and ESC Cardiovascular Risk Collaboration,
 * "SCORE2 risk prediction algorithms: new models to estimate 10-year risk of
 * cardiovascular disease in Europe", European Heart Journal 2021;42:2439-2454,
 * plus the updated supplementary material (page 9) that publishes the
 * coefficients, the baseline survival and the regional recalibration scales
 * reproduced below. Every number here is quoted from that table; none is
 * fitted, rounded or interpolated by us.
 *
 * Pure data plus arithmetic, and in `lib/` rather than beside the route so the
 * dependency points the way the rest of the tree does (app -> lib, never
 * back): the widget, the on-page threshold table AND the markdown mirror in
 * `lib/native-markdown/score2-calculator.ts` read this one source, so a
 * changed coefficient cannot drift between them. It is also unit-testable
 * without rendering React.
 */

/**
 * The sex term in the model. Named for what it is, one of two published
 * coefficient sets, not a claim about the reader: SCORE2 was derived as two
 * separate sex-specific models with their own baseline survival and their own
 * recalibration scales, so there is no third column to offer and no way to
 * average them that the paper supports.
 */
export type Score2Sex = "female" | "male"

/**
 * The age range SCORE2 was derived and validated in.
 *
 * Hard bounds, not plausibility hints: below 40 the model has no derivation
 * cohort, and from 70 the ESC uses a different model entirely (SCORE2-OP, with
 * its own coefficients, its own baseline survival and its own scales). Feeding
 * a 75-year-old into the equations below returns a number, which is exactly
 * why the widget must refuse to instead of printing it.
 */
export const SCORE2_MIN_AGE = 40
export const SCORE2_MAX_AGE = 69

/**
 * Plausibility bounds on the measured inputs. Outside these a value is almost
 * certainly a typo or the wrong unit, and the model is not calibrated there.
 *
 * Cholesterol is in mmol/L, which is what Swiss labs report and what the
 * published equations take. There is no mg/dL field and no converter: a unit
 * switcher on this page would be the one place a reader could silently feed
 * 200 into a term expecting 5.2.
 */
export const MIN_SBP = 80
export const MAX_SBP = 220
export const MIN_TOTAL_CHOLESTEROL = 2
export const MAX_TOTAL_CHOLESTEROL = 12
export const MIN_HDL = 0.3
export const MAX_HDL = 4

/**
 * The unit bridge for the two lipids, for the form rather than for the model.
 *
 * Nothing in this file computes with it: SCORE2's terms take mmol/L, which is
 * what the paper fitted and what Swiss laboratories report. German
 * laboratories report cholesterol in mg/dL, so the form there asks in mg/dL
 * and converts before calling anything here (feature:tools-mgdl-lipids,
 * lib/lipid-units.ts). 1 mmol/L of cholesterol is 38.67 mg/dL, from its molar
 * mass of 386.65 g/mol; the same factor serves total cholesterol and HDL,
 * since both are the same molecule.
 *
 * A constant with a name rather than an inline `* 38.67`, for the reason the
 * MAFLD page's triglyceride factor is one: a wrong factor moves every result
 * by a fixed amount and nothing about the output looks broken.
 */
export const CHOLESTEROL_MG_DL_PER_MMOL_L = 38.67

/**
 * The four CVD risk regions SCORE2 is recalibrated to, ranked by the WHO's
 * age- and sex-standardised cardiovascular mortality rate for the country.
 * The uncalibrated model is one model for all of Europe; the region is what
 * turns it into a local estimate.
 */
export type Score2Region = "low" | "moderate" | "high" | "veryHigh"

/**
 * Which region each market Ahead serves belongs to.
 *
 * Deliberately partial, and deliberately the only way to reach the scales: the
 * region is the market-divergent half of this model in exactly the way a price
 * is elsewhere on the site, and getting it wrong is silent. Switzerland is a
 * LOW risk region country (with Belgium, Denmark, France, Israel, Luxembourg,
 * Norway, Spain, the Netherlands and the United Kingdom); Germany and Austria
 * are MODERATE. A German reader scored on the Swiss calibration would be told
 * a materially lower risk than the ESC's own German figure, on a page that
 * looks completely correct: the worked example both pages print, a 60-year-old
 * man at 140 mmHg and 6.0 mmol/L, is 6.1% on the low scales and 7.8% on the
 * moderate ones, which is about a quarter more and enough to move some
 * readers across a treatment threshold.
 *
 * Adding a market means adding its entry HERE, not only translating the copy
 * and widening `page:tools/score2-calculator`. Until it is added,
 * `score2RegionForCountry` returns undefined and the widget declines to
 * compute rather than falling back to a region. The Netherlands landed in the
 * same change as its page, as that rule asks: it is LOW, the same region as
 * Switzerland, so /nl/nl and /nl/en score the page's worked example at the
 * same 6.1% the Swiss pages print, and the copy there names the Netherlands
 * rather than repeating Switzerland's claim. Austria is still absent because
 * its page is, not because its region is unknown: AT is moderate like
 * Germany, and that entry belongs in the change that gives it the page.
 */
export const SCORE2_REGION_BY_COUNTRY: Partial<Record<Country, Score2Region>> =
  {
    ch: "low",
    de: "moderate",
    nl: "low",
  }

/** The region to score a visitor in `country` with, or undefined if unwired. */
export function score2RegionForCountry(
  country: Country
): Score2Region | undefined {
  return SCORE2_REGION_BY_COUNTRY[country]
}

/**
 * Sex-specific model coefficients (EHJ 2021 supplementary material).
 *
 * Applied to the paper's centred and scaled transformations, not to raw
 * values: see `linearPredictor` for the four transformations, which are part
 * of the published model and not a normalisation of our own.
 *
 * The diabetes term the paper also lists is intentionally absent. It exists
 * only because the recalibration procedure needed whole-population data;
 * SCORE2 is not intended for, and was not validated in, people with diabetes,
 * for whom the ESC publishes SCORE2-Diabetes instead. In SCORE2's target
 * population the term is always zero, so carrying it would add an input the
 * page must then refuse to honour.
 */
const COEFFICIENTS: Record<
  Score2Sex,
  {
    age: number
    smoking: number
    sbp: number
    totalCholesterol: number
    hdl: number
    ageSmoking: number
    ageSbp: number
    ageTotalCholesterol: number
    ageHdl: number
  }
> = {
  male: {
    age: 0.3742,
    smoking: 0.6012,
    sbp: 0.2777,
    totalCholesterol: 0.1458,
    hdl: -0.2698,
    ageSmoking: -0.0755,
    ageSbp: -0.0255,
    ageTotalCholesterol: -0.0281,
    ageHdl: 0.0426,
  },
  female: {
    age: 0.4648,
    smoking: 0.7744,
    sbp: 0.3131,
    totalCholesterol: 0.1002,
    hdl: -0.2606,
    ageSmoking: -0.1088,
    ageSbp: -0.0277,
    ageTotalCholesterol: -0.0226,
    ageHdl: 0.0613,
  },
}

/** Sex-specific 10-year baseline survival of the uncalibrated model. */
const BASELINE_SURVIVAL: Record<Score2Sex, number> = {
  male: 0.9605,
  female: 0.9776,
}

/**
 * Regional recalibration scales for the under-70 model, by region and sex.
 *
 * All four regions are quoted, not just Switzerland's, because
 * `SCORE2_REGION_BY_COUNTRY` is what selects between them: keeping the whole
 * published table here means opening the page to a new market is that map's
 * one-line entry rather than a second trip to the supplementary material.
 */
const REGION_SCALES: Record<
  Score2Region,
  Record<Score2Sex, { scale1: number; scale2: number }>
> = {
  low: {
    male: { scale1: -0.5699, scale2: 0.7476 },
    female: { scale1: -0.738, scale2: 0.7019 },
  },
  moderate: {
    male: { scale1: -0.1565, scale2: 0.8009 },
    female: { scale1: -0.3143, scale2: 0.7701 },
  },
  high: {
    male: { scale1: 0.3207, scale2: 0.936 },
    female: { scale1: 0.571, scale2: 0.9369 },
  },
  veryHigh: {
    male: { scale1: 0.5836, scale2: 0.8294 },
    female: { scale1: 0.9412, scale2: 0.8329 },
  },
}

export interface Score2Input {
  sex: Score2Sex
  /** Years. */
  age: number
  /** Current smoker. The model has no "former smoker" term. */
  smoker: boolean
  /** Systolic blood pressure, mmHg. */
  systolicBp: number
  /** Total cholesterol, mmol/L. */
  totalCholesterol: number
  /** HDL cholesterol, mmol/L. */
  hdlCholesterol: number
}

/**
 * The published linear predictor: each risk factor centred and scaled as the
 * paper specifies, plus an age interaction for every one of them.
 *
 *   cage   = (age - 60) / 5
 *   csbp   = (systolic BP - 120) / 20
 *   ctchol = total cholesterol - 6
 *   chdl   = (HDL - 1.3) / 0.5
 *
 * The age interactions are why SCORE2 cannot be read off a simple points
 * table: the same 20 mmHg costs a 45-year-old more than a 65-year-old,
 * because `ageSbp` is negative.
 */
function linearPredictor({
  sex,
  age,
  smoker,
  systolicBp,
  totalCholesterol,
  hdlCholesterol,
}: Score2Input): number {
  const c = COEFFICIENTS[sex]
  const cage = (age - 60) / 5
  const csbp = (systolicBp - 120) / 20
  const ctchol = totalCholesterol - 6
  const chdl = (hdlCholesterol - 1.3) / 0.5
  const smoking = smoker ? 1 : 0

  return (
    c.age * cage +
    c.smoking * smoking +
    c.sbp * csbp +
    c.totalCholesterol * ctchol +
    c.hdl * chdl +
    c.ageSmoking * cage * smoking +
    c.ageSbp * cage * csbp +
    c.ageTotalCholesterol * cage * ctchol +
    c.ageHdl * cage * chdl
  )
}

/**
 * The 10-year risk of fatal or non-fatal CVD, as a percentage, for a reader in
 * `region`.
 *
 * Two steps, both from the paper:
 *
 *   1. uncalibrated risk = 1 - S0 ^ exp(linear predictor)
 *   2. calibrated risk   = 1 - exp(-exp(s1 + s2 · ln(-ln(1 - uncalibrated))))
 *
 * The second step is not cosmetic. On the low-risk scales a Swiss man's
 * calibrated risk is roughly half the uncalibrated model's, so skipping it, or
 * applying another region's scales, changes which side of a treatment
 * threshold the reader lands on.
 *
 * Returns the unrounded percentage; callers display and classify
 * `roundRisk()` of it, never this value directly (see `roundRisk`).
 */
export function score2Risk(input: Score2Input, region: Score2Region): number {
  const uncalibrated =
    1 - Math.pow(BASELINE_SURVIVAL[input.sex], Math.exp(linearPredictor(input)))
  const { scale1, scale2 } = REGION_SCALES[region][input.sex]
  const calibrated =
    1 - Math.exp(-Math.exp(scale1 + scale2 * Math.log(-Math.log(1 - uncalibrated))))
  return calibrated * 100
}

/**
 * The risk as it is shown to the reader: one decimal place, which is what the
 * ESC's own calculators print.
 *
 * Callers must classify THIS value, not the raw one. Classifying the raw value
 * and displaying the rounded one lets the two disagree at a band edge: a raw
 * 4.96% displays as "5.0%" but classifies as low-to-moderate, so a 55-year-old
 * would see "5.0%" next to a band the page's own threshold table puts at
 * "5% to under 10%". Rounding first means the label always describes the
 * number on screen.
 */
export function roundRisk(percent: number): number {
  return Math.round(percent * 10) / 10
}

/** Why an otherwise complete set of inputs cannot be scored. */
export type Score2Problem = "ageOutOfRange" | "outOfRange" | "hdlAboveTotal"

/**
 * The first reason `input` cannot be scored, or null when it can.
 *
 * Age is checked separately from the rest because it is the one bound with a
 * real answer behind it: a 74-year-old has not mistyped anything, SCORE2 is
 * simply the wrong model for them, and the page says so (SCORE2-OP) rather
 * than telling them to check their entry.
 *
 * The HDL-above-total case is its own message for the same reason. It is a
 * lab-report mix-up, not an implausible value: both numbers can sit inside
 * their own bounds while implying a negative non-HDL cholesterol, which the
 * model would happily score.
 */
export function score2Problem(input: Score2Input): Score2Problem | null {
  if (input.age < SCORE2_MIN_AGE || input.age > SCORE2_MAX_AGE) {
    return "ageOutOfRange"
  }
  if (
    input.systolicBp < MIN_SBP ||
    input.systolicBp > MAX_SBP ||
    input.totalCholesterol < MIN_TOTAL_CHOLESTEROL ||
    input.totalCholesterol > MAX_TOTAL_CHOLESTEROL ||
    input.hdlCholesterol < MIN_HDL ||
    input.hdlCholesterol > MAX_HDL
  ) {
    return "outOfRange"
  }
  if (input.hdlCholesterol >= input.totalCholesterol) {
    return "hdlAboveTotal"
  }
  return null
}

// ─── Risk bands and the age groups they are cut by ──────────────────────────

export type Score2Band = Score2BandDef["id"]

/**
 * The three CVD risk categories from the 2021 ESC prevention guidelines, low
 * to high. `score2BandFor` walks this in order.
 *
 * `as const` is load-bearing here for the same reason it is in the BMI
 * calculator's `bmi.ts`: annotating the `*Key` fields as
 * `MessageKeys<"score2Calculator">` would widen them to the namespace's whole
 * key union and make next-intl's typed `t()` demand a `values` argument at
 * every `t(band.labelKey)` call site.
 */
const BAND_DEFS = [
  {
    id: "lowToModerate",
    labelKey: k.bandLowToModerate,
    meaningKey: k.meaningLowToModerate,
  },
  { id: "high", labelKey: k.bandHigh, meaningKey: k.meaningHigh },
  { id: "veryHigh", labelKey: k.bandVeryHigh, meaningKey: k.meaningVeryHigh },
] as const

export type Score2BandDef = (typeof BAND_DEFS)[number]

export const BAND_ORDER: readonly Score2BandDef[] = BAND_DEFS

export const SCORE2_BANDS: Record<Score2Band, Score2BandDef> =
  Object.fromEntries(BAND_DEFS.map((b) => [b.id, b])) as Record<
    Score2Band,
    Score2BandDef
  >

export type Score2AgeGroup = Score2AgeGroupDef["id"]

/**
 * The ESC's risk thresholds are age-banded: the same 6% means "high" for a
 * 55-year-old and "very high" for a 45-year-old, because a young person's
 * 10-year risk understates their lifetime risk.
 *
 * All three groups are here, including 70 and over, which this calculator does
 * NOT score. The reference table on the page shows the full guideline so a
 * reader can see where they sit; the widget refuses the age and points at
 * SCORE2-OP. Dropping the row would make the page's own table look like it
 * stops at 69 by clinical fact rather than by which model applies.
 */
const AGE_GROUP_DEFS = [
  {
    id: "under50",
    /** Exclusive upper bound; `Infinity` closes the walk. */
    maxAge: 50,
    labelKey: k.ageUnder50,
    highFrom: 2.5,
    veryHighFrom: 7.5,
    thresholdKeys: [
      k.thrUnder50LowToModerate,
      k.thrUnder50High,
      k.thrUnder50VeryHigh,
    ],
  },
  {
    id: "from50to69",
    maxAge: 70,
    labelKey: k.age50to69,
    highFrom: 5,
    veryHighFrom: 10,
    thresholdKeys: [
      k.thr50to69LowToModerate,
      k.thr50to69High,
      k.thr50to69VeryHigh,
    ],
  },
  {
    id: "from70",
    maxAge: Infinity,
    labelKey: k.age70plus,
    highFrom: 7.5,
    veryHighFrom: 15,
    thresholdKeys: [
      k.thr70plusLowToModerate,
      k.thr70plusHigh,
      k.thr70plusVeryHigh,
    ],
  },
] as const

export type Score2AgeGroupDef = (typeof AGE_GROUP_DEFS)[number]

export const AGE_GROUP_ORDER: readonly Score2AgeGroupDef[] = AGE_GROUP_DEFS

/** Which ESC age group an age falls into. */
export function score2AgeGroupFor(age: number): Score2AgeGroupDef {
  for (const group of AGE_GROUP_ORDER) {
    if (age < group.maxAge) return group
  }
  return AGE_GROUP_DEFS[2]
}

/**
 * Which band a risk falls into for someone of this age. Boundaries are
 * inclusive at the bottom of each band: for a 55-year-old exactly 5.0% is
 * high risk and exactly 10.0% is very high.
 *
 * Pass a value from `roundRisk` (see the note there).
 */
export function score2BandFor(age: number, riskPercent: number): Score2Band {
  const { highFrom, veryHighFrom } = score2AgeGroupFor(age)
  if (riskPercent >= veryHighFrom) return "veryHigh"
  if (riskPercent >= highFrom) return "high"
  return "lowToModerate"
}
